import { FC, useEffect, useMemo, useState } from 'react';
import styles from './Motivation.module.scss'
import { Checkbox } from 'antd';
import FlexColumn from '../../../../../components/controls/FlexColumn';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../../../../../system/providers/appContextProvider';
import {
    IContractorMotivationDTO,
    IContractorMotivationEditDTO,
    IContractorMotivationViewDTO,
    IIlliquidGoodsDTO,
    IIlliquidGoodsStoresDTO,
    IIlliquidGoodsStoresEditDTO,
    IIsgGoodsDTO,
    IIsgGoodsStoresDTO,
    IIsgGoodsStoresEditDTO,
    IMotivationBonusDTO,
    IMotivationBonusEditDTO,
    IMotivationGoodsGroupEditDTO,
    IMotivationStoreEditDTO
} from '../../../../../libs/coreapi-dto/dirs/contractorsMotivation';
import { ContractorMotivationDataProvider } from '../../../../../Services/DataProviders/ContractorMotivationDataProvider';
import GridWrapper from '../../../../../components/controls/GridWrapper';
import { useContractorMotivation } from './useMotivation';
import { IOption, Select } from '../../../../../components/selects/select';
import { IEntitySimpleDTO } from '../../../../../libs/coreapi-dto/@types/common';
import { DateTime } from 'luxon';
import { v4 as uuidv4 } from "uuid"
import GoodsTable from './GoodsTable';
import BonusCard from './BonusCard';
import GridMultipleSelectorInputV2 from '../../../../../components/controls/GridMultipleSelectorInputV2';
import useGridFilter from '../../../../../system/hooks/useGridFilter';
import StoreSelectorModal from '../../../Store/StoreSelectorModal';
import { Spinner } from '../../../../../components/spiner/Spinner';
import { IMotivationTabProps } from './.interface';
import { DecimalInputWithMinMax } from '../../../../../components/controls/inputs';

const MotivationTab: FC<IMotivationTabProps> = (props) => {
    const appContext = useAppContext();
    const motivationDP = new ContractorMotivationDataProvider(appContext.coreApiService)
    const [gridFilter, dispatchGridFilter] = useGridFilter({
        numberPerPage: 15,
        pageNumber: 1,
        columnFilters: [
            {
                name: "deleted",
                operator: "Eq",
                value: false,
            },
            {
                name: "idContractorGlobal",
                operator: "Eq",
                value: props.idGlobal,
            },
        ]
    });

    const {
        motivation,
        setMotivationData,
        resetToDefault,
        updateBonusMzActive,
        updateBonusMz,
        updateKppm,
        updateIsgGoodsStores,
        updateIlliquidGoodsStores,
        updateMotivationActiveIsgGoods,
        updateMotivationActiveIsgGoodsStores,
        updateMotivationActiveIlliquidGoodsStores,
        addBonusToMotivation,
        removeBonus,
        updateBonusStores,
        updateBonusGoodsGroups,
        updateBonusField,
        updateMotivationPercent,
        updateMotivationActiveInternetOrder,
        updateMotivationActiveAddSaleInternetOrder,
        updatePeriodReglBuildInternetOrder,
        updateSumPenaltyInternetOrder,
        updateSumPremiumInternetOrder,
        updateBonusAddSaleInternetOrder,
        updateBonusUnitAddSaleInternetOrder,
        updateMotivationActiveIlliquidGoods,
        updateSkipProfitCheckType,
        updateMarginCode,
        setIsgGoods,
        setIlliquidGoods
    } = useContractorMotivation(undefined, props.idGlobal);

    const [viewMotivation, setViewMotivation] = useState<IContractorMotivationViewDTO>();

    const { t } = useTranslation();
    const baseT = (value: string) => t('directory.contractors.motivationTab.' + value);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const selectedIsgGoodsStores: IEntitySimpleDTO[] = viewMotivation?.isgGoodsStores.map(store => store.store) || [];
    const selectedIlliquidGoodsStores: IEntitySimpleDTO[] = viewMotivation?.illiquidGoodsStores.map(store => store.store) || [];

    const skipProfitCheckTypeOptions: IOption[] = [
        { displayName: baseT('skipProfitCheckTypeOptions.information'), value: 0 },
        { displayName: baseT('skipProfitCheckTypeOptions.accountingInMotivation'), value: 1 }
    ];

    const marginCodeOptions: IOption[] = [
        { displayName: baseT('marginCodeOptions.totalMargin'), value: 0 },
        { displayName: baseT('marginCodeOptions.totalMarginWithoutDiscount'), value: 4 },
        { displayName: baseT('marginCodeOptions.revenue'), value: 1 },
        { displayName: baseT('marginCodeOptions.revenueWithOutDiscount'), value: 5 },
        { displayName: baseT('marginCodeOptions.directMargin'), value: 2 },
        { displayName: baseT('marginCodeOptions.marginWithOutDiscount'), value: 6 },
        { displayName: baseT('marginCodeOptions.backMargin'), value: 3 }
    ];

    const bonusUnitAddSaleInternetOrderOptions: IOption[] = [
        { displayName: baseT('bonusUnitAddSaleInternetOrderOptions.percentOfTotalMargin'), value: 0 },
        { displayName: baseT('bonusUnitAddSaleInternetOrderOptions.percentOfRetailPrice'), value: 1 },
        { displayName: baseT('bonusUnitAddSaleInternetOrderOptions.ruble'), value: 2 }
    ];

    const [skipProfitCheckType, setSkipProfitCheckType] = useState<IOption>(() => {
        const option = skipProfitCheckTypeOptions.find(opt => opt.value === motivation.skipProfitCheckType);
        return option || skipProfitCheckTypeOptions[0];
    });

    const [marginCode, setMarginCode] = useState<IOption>(() => {
        const option = marginCodeOptions.find(opt => opt.value === motivation.marginCode);
        return option || marginCodeOptions[0];
    });

    const [bonusUnitAddSaleInternetOrder, setBonusUnitAddSaleInternetOrder] = useState<IOption>(() => {
        const option = bonusUnitAddSaleInternetOrderOptions.find(opt => opt.value === motivation.bonusUnitAddSaleInternetOrder);
        return option || bonusUnitAddSaleInternetOrderOptions[0];
    });

    const [isIsgGoodsEditing, setIsIsgGoodsEditing] = useState(false);
    const [isIlliquidGoodsEditing, setIsIlliquidGoodsEditing] = useState(false);

    const canSave = !isIsgGoodsEditing && !isIlliquidGoodsEditing;

    const isAllRowsIlliquidGoodsDeleted = useMemo(
        () => motivation.illiquidGoods.some(item => item.dateDeleted === null),
        [motivation.illiquidGoods]
    );
    const isAllRowsIsgGoodsDeleted = useMemo(
        () => motivation.isgGoods.some(item => item.dateDeleted === null),
        [motivation.isgGoods]
    );
    const isRowsDeleted = !isAllRowsIlliquidGoodsDeleted && motivation.motivationActiveIlliquidGoods || !isAllRowsIsgGoodsDeleted && motivation.motivationActiveIsgGoods

    useEffect(() => {
        props.onEditingChange?.(!canSave);
    }, [canSave, props.onEditingChange]);

    useEffect(() => {
        props.onRowsDeleted?.(isRowsDeleted);
    }, [isRowsDeleted, props.onRowsDeleted]);

    const transformToEditDTO = (data: IContractorMotivationDTO): IContractorMotivationEditDTO => {
        return {
            ...data,
            bonuses: data.bonuses?.map((bonus: IMotivationBonusDTO): IMotivationBonusEditDTO => ({
                ...bonus,
                stores: bonus.stores?.map((store: any): IMotivationStoreEditDTO => ({
                    idGlobal: store.idGlobal,
                    dateCreated: store.dateCreated,
                    dateModified: store.dateModified,
                    dateDeleted: store.dateDeleted,
                    idContractorMotivationBonusGlobal: store.idContractorMotivationBonusGlobal,
                    idStoreGlobal: store.store?.idGlobal || store.idStoreGlobal
                })) || [],
                goodsGroups: bonus.goodsGroups?.map((group: any): IMotivationGoodsGroupEditDTO => ({
                    idGlobal: group.idGlobal,
                    dateCreated: group.dateCreated,
                    dateModified: group.dateModified,
                    dateDeleted: group.dateDeleted,
                    idContractorMotivationBonusGlobal: group.idContractorMotivationBonusGlobal,
                    idGoodsGroupGlobal: group.goodsGroup?.idGlobal || group.idGoodsGroupGlobal
                })) || []
            })) || [],
            illiquidGoodsStores: data.illiquidGoodsStores?.map((store: IIlliquidGoodsStoresDTO): IIlliquidGoodsStoresEditDTO => ({
                ...store,
                idGlobal: store.idGlobal,
                dateCreated: store.dateCreated,
                dateModified: store.dateModified,
                dateDeleted: store.dateDeleted,
                idStoreGlobal: store.store.idGlobal,
                idContractorMotivationGlobal: store.idContractorMotivationGlobal,
            })) || [],
            isgGoodsStores: data.illiquidGoodsStores?.map((store: IIsgGoodsStoresDTO): IIsgGoodsStoresEditDTO => ({
                ...store,
                idGlobal: store.idGlobal,
                dateCreated: store.dateCreated,
                dateModified: store.dateModified,
                dateDeleted: store.dateDeleted,
                idStoreGlobal: store.store.idGlobal,
                idContractorMotivationGlobal: store.idContractorMotivationGlobal,
            })) || []

        };
    };

    useEffect(() => {
        setSkipProfitCheckType(
            skipProfitCheckTypeOptions.find(opt => opt.value === motivation.skipProfitCheckType) || skipProfitCheckTypeOptions[0]
        );
        setMarginCode(
            marginCodeOptions.find(opt => opt.value === motivation.marginCode) || marginCodeOptions[0]
        );
    }, [motivation.skipProfitCheckType, motivation.marginCode]);

    useEffect(() => {
        if (motivation && props.onDataChange) {
            props.onDataChange(motivation);
        }
    }, [motivation]);

    useEffect(() => {
        loadMotivationData();
    }, [props.idGlobal]);

    const loadMotivationData = async () => {
        const controller = new AbortController();
        try {
            setIsLoading(true);
            await new Promise<void>((resolve, reject) => {
                motivationDP.getByIdContractorGlobal(props.idGlobal, (data?: IContractorMotivationDTO, error?: any) => {
                    if (error) {
                        reject(error);
                        return;
                    }
                    try {
                        if (data) {
                            setViewMotivation(data);
                            const editData = transformToEditDTO(data);
                            setMotivationData(editData);
                        } else {
                            resetToDefault();
                        }
                        resolve();
                    } catch (err) {
                        reject(err);
                    }
                });
            });
        } catch (error: any) {
            if (error?.title?.includes('contractor_motivation') && error?.title?.includes('не найдена')) {
                resetToDefault();
            } else {
                console.error(error);
            }
        } finally {
            setIsLoading(false);
        }
        return () => controller.abort()
    };

    const handleIsgGoodsChange = (newIsgGoods: IIsgGoodsDTO[]) => {
        setIsgGoods(newIsgGoods);
    };

    const handleIilliquidGoodsChange = (newIilliquidGoodsChange: IIlliquidGoodsDTO[]) => {
        setIlliquidGoods(newIilliquidGoodsChange);
    };

    const handleBonusStoresChange = (bonusIndex: number, stores: IEntitySimpleDTO[]) => {
        const storeEdits: IMotivationStoreEditDTO[] = stores.map(store => ({
            idGlobal: uuidv4(),
            dateCreated: DateTime.now(),
            dateModified: DateTime.now(),
            dateDeleted: null,
            idContractorMotivationBonusGlobal: motivation.bonuses[bonusIndex].idGlobal,
            idStoreGlobal: store.idGlobal
        }));

        updateBonusStores(bonusIndex, storeEdits);

        if (viewMotivation) {
            const updatedViewMotivation = { ...viewMotivation };
            const viewBonusIndex = updatedViewMotivation.bonuses.findIndex(
                vb => vb.idGlobal === motivation.bonuses[bonusIndex].idGlobal
            );

            if (viewBonusIndex !== -1) {
                updatedViewMotivation.bonuses[viewBonusIndex].stores = stores.map(store => ({
                    idGlobal: uuidv4(),
                    dateCreated: DateTime.now(),
                    dateModified: DateTime.now(),
                    dateDeleted: null,
                    idContractorMotivationBonusGlobal: motivation.bonuses[bonusIndex].idGlobal,
                    store: store
                }));
                setViewMotivation(updatedViewMotivation);
            }
        }
    };

    const handleIsgGoodsStoresChange = (stores: IEntitySimpleDTO[]) => {
        const storeEdits: IIsgGoodsStoresEditDTO[] = stores.map(store => ({
            idGlobal: uuidv4(),
            dateCreated: DateTime.now(),
            dateModified: DateTime.now(),
            dateDeleted: null,
            idContractorMotivationGlobal: motivation.idGlobal,
            idStoreGlobal: store.idGlobal
        }));

        updateIsgGoodsStores(storeEdits);

        if (viewMotivation) {
            setViewMotivation({
                ...viewMotivation,
                isgGoodsStores: stores.map(store => ({
                    idGlobal: uuidv4(),
                    dateCreated: DateTime.now(),
                    dateModified: DateTime.now(),
                    dateDeleted: null,
                    idContractorMotivationGlobal: motivation.idGlobal,
                    store: store
                }))
            });
        }
    };

    const handleIlliquidGoodsStoresChange = (stores: IEntitySimpleDTO[]) => {
        const storeEdits: IIlliquidGoodsStoresEditDTO[] = stores.map(store => ({
            idGlobal: uuidv4(),
            dateCreated: DateTime.now(),
            dateModified: DateTime.now(),
            dateDeleted: null,
            idContractorMotivationGlobal: motivation.idGlobal,
            idStoreGlobal: store.idGlobal
        }));

        updateIlliquidGoodsStores(storeEdits);

        if (viewMotivation) {
            setViewMotivation({
                ...viewMotivation,
                illiquidGoodsStores: stores.map(store => ({
                    idGlobal: uuidv4(),
                    dateCreated: DateTime.now(),
                    dateModified: DateTime.now(),
                    dateDeleted: null,
                    idContractorMotivationGlobal: motivation.idGlobal,
                    store: store
                }))
            });
        }
    };

    const handleIsgGoodsStoresClear = () => {
        updateIsgGoodsStores([]);

        if (viewMotivation) {
            setViewMotivation({
                ...viewMotivation,
                isgGoodsStores: []
            });
        }
    };

    const handleIlliquidGoodsStoresClear = () => {
        updateIlliquidGoodsStores([]);

        if (viewMotivation) {
            setViewMotivation({
                ...viewMotivation,
                illiquidGoodsStores: []
            });
        }
    };

    const handleBonusGoodsGroupsChange = (bonusIndex: number, goodsGroups: IEntitySimpleDTO[]) => {
        const groupEdits: IMotivationGoodsGroupEditDTO[] = goodsGroups.map(group => ({
            idGlobal: uuidv4(),
            dateCreated: DateTime.now(),
            dateModified: DateTime.now(),
            dateDeleted: null,
            idContractorMotivationBonusGlobal: motivation.bonuses[bonusIndex].idGlobal,
            idGoodsGroupGlobal: group.idGlobal
        }));

        updateBonusGoodsGroups(bonusIndex, groupEdits);

        if (viewMotivation) {
            const updatedViewMotivation = { ...viewMotivation };
            const viewBonusIndex = updatedViewMotivation.bonuses.findIndex(
                vb => vb.idGlobal === motivation.bonuses[bonusIndex].idGlobal
            );

            if (viewBonusIndex !== -1) {
                updatedViewMotivation.bonuses[viewBonusIndex].goodsGroups = goodsGroups.map(group => ({
                    idGlobal: uuidv4(),
                    dateCreated: DateTime.now(),
                    dateModified: DateTime.now(),
                    dateDeleted: null,
                    idContractorMotivationBonusGlobal: motivation.bonuses[bonusIndex].idGlobal,
                    goodsGroup: group
                }));
                setViewMotivation(updatedViewMotivation);
            }
        }
    };

    const addBonus = () => {
        const newBonus: IMotivationBonusEditDTO = {
            idGlobal: uuidv4(),
            dateCreated: DateTime.now(),
            dateModified: DateTime.now(),
            dateDeleted: null,
            idContractorMotivationGlobal: motivation.idGlobal,
            value: 0,
            marginCode: 0,
            enabledStores: false,
            stores: [],
            enabledGoodsGroups: false,
            goodsGroups: []
        };

        addBonusToMotivation(newBonus);

        if (viewMotivation) {
            const newViewBonus: IMotivationBonusDTO = {
                ...newBonus,
                stores: [],
                goodsGroups: []
            };

            setViewMotivation({
                ...viewMotivation,
                bonuses: [...viewMotivation.bonuses, newViewBonus]
            });
        }
    };

    if (isLoading) return (<Spinner xl />)

    return (
        <>
            <FlexColumn>
                {/* Бонус за  регистрацию ПЛ ПА */}
                <div className={`${styles.baseBlockWithoutBorder} ${styles.bottomMargin}`}>
                    <GridWrapper cols={2}>
                        <div className={styles.rowWrapper}>
                            <Checkbox
                                checked={motivation.bonusMzActive}
                                onChange={(e) => updateBonusMzActive(e.target.checked)}
                                name="bonusMzActive"
                            >
                                {baseT('bonusForRegistration')}
                            </Checkbox>
                        </div>
                        <DecimalInputWithMinMax
                            min={0}
                            max={99.99}
                            value={motivation.bonusMz}
                            onChange={updateBonusMz}
                            maxDecimalPlaces={1}
                            disabled={!motivation.bonusMzActive}
                        />
                    </GridWrapper>
                    <GridWrapper cols={2}>
                        <div className={styles.rowWrapper}>{baseT('skipProfitCheckType')}</div>
                        <Select
                            style={{ margin: 0 }}
                            value={skipProfitCheckType}
                            options={skipProfitCheckTypeOptions}
                            onSelect={(option: IOption) => {
                                setSkipProfitCheckType(option);
                                updateSkipProfitCheckType(Number(option.value));
                            }}
                            disabled={!motivation.bonusMzActive}
                        />
                    </GridWrapper>
                    <GridWrapper cols={2}>
                        <div className={styles.rowWrapper}>{baseT('motivationPercent')}</div>
                        <DecimalInputWithMinMax
                            min={0}
                            value={motivation.motivationPercent}
                            onChange={updateMotivationPercent}
                            disabled={!motivation.bonusMzActive}
                        />
                    </GridWrapper>
                    <GridWrapper cols={2}>
                        <div className={styles.rowWrapper}>{baseT('marginCode')}</div>
                        <Select
                            style={{ margin: 0 }}
                            value={marginCode}
                            options={marginCodeOptions}
                            onSelect={(option: IOption) => {
                                setMarginCode(option);
                                updateMarginCode(Number(option.value));
                            }}
                            disabled={!motivation.bonusMzActive}
                        />
                    </GridWrapper>
                    {/*КППМ*/}
                    <GridWrapper cols={2}>
                        <div className={styles.rowWrapper}>{baseT('kppm')}</div>
                        <DecimalInputWithMinMax
                            min={0.01}
                            max={100}
                            value={motivation.kppm}
                            onChange={updateKppm}
                            disabled={motivation.marginCode !== 0 && motivation.marginCode !== 4}
                        />
                    </GridWrapper>
                </div>
                {/*  Мотивация по бронированию */}
                <div className={`${styles.baseBlock} ${styles.bottomMargin}`}>
                    <div className={styles.checkBoxForBlockWithBorder}>
                        <Checkbox
                            checked={motivation.motivationActiveInternetOrder}
                            onChange={(e) => updateMotivationActiveInternetOrder(e.target.checked)}
                            name="motivationActiveInternetOrder"
                            disabled
                        >
                            {baseT('motivationForBooking')}
                        </Checkbox>
                    </div>
                    <GridWrapper cols={2}>
                        <div className={styles.rowWrapper}>{baseT('periodReglBuildInternetOrder')}</div>
                        <DecimalInputWithMinMax
                            min={0}
                            maxDecimalPlaces={0}
                            value={motivation.periodReglBuildInternetOrder}
                            onChange={updatePeriodReglBuildInternetOrder}
                            disabled={!motivation.motivationActiveInternetOrder}
                        />
                    </GridWrapper>
                    <GridWrapper cols={2}>
                        <div className={styles.rowWrapper}>{baseT('sumPenaltyInternetOrder')}</div>
                        <DecimalInputWithMinMax
                            min={0}
                            maxDecimalPlaces={2}
                            value={motivation.sumPenaltyInternetOrder}
                            onChange={updateSumPenaltyInternetOrder}
                            disabled={!motivation.motivationActiveInternetOrder}
                        />
                    </GridWrapper>
                    <GridWrapper cols={2}>
                        <div className={styles.rowWrapper}>{baseT('sumPremiumInternetOrder')}</div>
                        <DecimalInputWithMinMax
                            min={0}
                            maxDecimalPlaces={2}
                            value={motivation.sumPremiumInternetOrder}
                            onChange={updateSumPremiumInternetOrder}
                            disabled={!motivation.motivationActiveInternetOrder}
                        />
                    </GridWrapper>
                </div>
                {/*  Мотивация за доп продажу */}
                <div className={` ${styles.bottomMargin}`}>
                    <GridWrapper cols={2}>
                        <div className={styles.rowWrapper}>
                            <Checkbox
                                checked={motivation.motivationActiveAddSaleInternetOrder}
                                onChange={(e) => updateMotivationActiveAddSaleInternetOrder(e.target.checked)}
                                name="motivationActiveAddSaleInternetOrder"
                                disabled
                            >
                                {baseT('motivationActiveAddSaleInternetOrder')}
                            </Checkbox>
                        </div>
                        <GridWrapper cols={2}>
                            <DecimalInputWithMinMax
                                min={0}
                                max={999}
                                maxDecimalPlaces={0}
                                value={motivation.bonusAddSaleInternetOrder}
                                onChange={updateBonusAddSaleInternetOrder}
                                disabled={!motivation.motivationActiveAddSaleInternetOrder}
                            />
                            <Select
                                style={{ marginTop: 8 }}
                                value={bonusUnitAddSaleInternetOrder}
                                options={bonusUnitAddSaleInternetOrderOptions}
                                onSelect={(option: IOption) => {
                                    setBonusUnitAddSaleInternetOrder(option);
                                    updateBonusUnitAddSaleInternetOrder(Number(option.value));
                                }}
                                disabled={!motivation.motivationActiveAddSaleInternetOrder}
                            />
                        </GridWrapper>
                    </GridWrapper>
                </div>

                {/*  Мотивация за товары с ИСГ */}
                <div className={`${styles.baseBlock} ${styles.bottomMargin}`}>
                    <div className={styles.checkBoxForBlockWithBorder}>
                        <Checkbox
                            checked={motivation.motivationActiveIsgGoods}
                            onChange={(e) => updateMotivationActiveIsgGoods(e.target.checked)}
                            name="motivationActiveIsgGoods"
                        >
                            {baseT('motivationActiveIsgGoods')}
                        </Checkbox>
                    </div>
                    <GridWrapper cols={1}>
                        <GoodsTable
                            data={motivation.isgGoods || []}
                            onChange={handleIsgGoodsChange}
                            contractorMotivationId={motivation.idGlobal}
                            disabled={!motivation.motivationActiveIsgGoods}
                            onEditingChange={setIsIsgGoodsEditing}
                            tableName={'isgGoods'}
                        />
                    </GridWrapper>
                    <GridWrapper cols={1}>
                        <Checkbox
                            checked={motivation.motivationActiveIsgGoodsStores}
                            onChange={(e) => {
                                const checked = e.target.checked;
                                updateMotivationActiveIsgGoodsStores(checked);
                                if (!checked) {
                                    handleIsgGoodsStoresClear()
                                }
                            }}
                            name="motivationActiveIsgGoodsStores"
                        >
                            {baseT('motivationActiveIsgGoodsStores')}
                        </Checkbox>
                    </GridWrapper>
                    <GridWrapper cols={1}>
                        <GridMultipleSelectorInputV2
                            className={styles.width100}
                            placeholder={baseT('enabledStores')}
                            selectedEntities={selectedIsgGoodsStores}
                            id="isgGoodsStores"
                            switchOnchange
                            gridFilter={gridFilter}
                            selectorModalJsx={StoreSelectorModal}
                            onSelect={handleIsgGoodsStoresChange}
                            onClear={handleIsgGoodsStoresClear}
                            isSearchForMultyselect
                            dataFromPluginGridSelectorInput={selectedIsgGoodsStores}
                            disabled={!motivation.motivationActiveIsgGoodsStores}
                        />
                    </GridWrapper>
                </div>
                {/*  Мотивация за неликвиды */}
                <div className={`${styles.baseBlock} ${styles.bottomMargin}`}>
                    <div className={styles.checkBoxForBlockWithBorder}>
                        <Checkbox
                            checked={motivation.motivationActiveIlliquidGoods}
                            onChange={(e) => updateMotivationActiveIlliquidGoods(e.target.checked)}
                            name="motivationActiveIlliquidGoods"
                        >
                            {baseT('motivationActiveIlliquidGoods')}
                        </Checkbox>
                    </div>
                    <GridWrapper cols={1}>
                        <GoodsTable
                            data={motivation.illiquidGoods || []}
                            onChange={handleIilliquidGoodsChange}
                            contractorMotivationId={motivation.idGlobal}
                            disabled={!motivation.motivationActiveIlliquidGoods}
                            onEditingChange={setIsIlliquidGoodsEditing}
                            tableName={'illiquidGoods'}
                        />
                    </GridWrapper>
                    <GridWrapper cols={1}>
                        <Checkbox
                            checked={motivation.motivationActiveIlliquidGoodsStores}
                            onChange={(e) => {
                                const checked = e.target.checked;
                                updateMotivationActiveIlliquidGoodsStores(checked);
                                if (!checked) {
                                    handleIlliquidGoodsStoresClear()
                                }
                            }}
                            name="motivationActiveIlliquidGoodsStores"
                        >
                            {baseT('motivationActiveIlliquidGoodsStores')}
                        </Checkbox>
                    </GridWrapper>
                    <GridWrapper cols={1}>
                        <GridMultipleSelectorInputV2
                            className={styles.width100}
                            placeholder={baseT('enabledStores')}
                            selectedEntities={selectedIlliquidGoodsStores}
                            id="illiquidGoodsStores"
                            switchOnchange
                            gridFilter={gridFilter}
                            selectorModalJsx={StoreSelectorModal}
                            onSelect={handleIlliquidGoodsStoresChange}
                            onClear={handleIlliquidGoodsStoresClear}
                            isSearchForMultyselect
                            dataFromPluginGridSelectorInput={selectedIlliquidGoodsStores}
                            disabled={!motivation.motivationActiveIlliquidGoodsStores}
                        />
                    </GridWrapper>
                </div>
                {/*  Бонусы */}
                {motivation.bonuses.map((bonus, index) => {
                    const viewBonus = viewMotivation?.bonuses.find(vb => vb.idGlobal === bonus.idGlobal) || {
                        ...bonus,
                        stores: bonus.stores.map(store => ({
                            ...store,
                            store: { idGlobal: store.idStoreGlobal, displayName: '' }
                        })),
                        goodsGroups: bonus.goodsGroups.map(group => ({
                            ...group,
                            goodsGroup: { idGlobal: group.idGoodsGroupGlobal, displayName: '' }
                        }))
                    };
                    if (viewBonus)
                        return (<BonusCard
                            key={bonus.idGlobal}
                            bonus={bonus}
                            viewBonus={viewBonus}
                            index={index}
                            onUpdate={updateBonusField}
                            isProAptekaEnabled={props.isProAptekaEnabled}
                            marginCodeOptions={marginCodeOptions}
                            idContractorGlobal={props.idGlobal}
                            baseT={baseT}
                            onStoresChange={handleBonusStoresChange}
                            onGoodsGroupsChange={handleBonusGoodsGroupsChange}
                            onAddNewBonus={addBonus}
                            onDeleteBonus={() => removeBonus(index)}
                        />)
                })}
            </FlexColumn>
        </>
    );
};

export default MotivationTab;